import React, { useState, useEffect } from 'react';
import { NavLink } from "react-router-dom";
import { ModalMessageType, DocumentType, Entities, GravimetricCalculationType, NoteType, Uoms } from '../models/enums';
import { BatchPlanningCalculation } from '../models/Calculations/batch-planning-calculation';
import BatchStandardsData from './BatchPlanning/BatchStandardsData';
import BatchPlanningToolbar from './BatchPlanningToolbar';
import BatchBenchSheetData from './BatchBenchSheetData';
import DocumentLoader from './DocumentLoader';
import BatchTargetWeightsData from './BatchTargetWeightsData';
import BatchAnalyticalPlansComponent from './BatchPlanning/BatchAnalyticalPlansComponent';
import { BatchPlan } from '../models/batch-plan';
import { FieldUpdate } from '../models/field-update';
import NoteViewer from './NoteViewer';
import { PWDates } from '../utilities/pw-dates';
import { CrudFunctions } from '../utilities/crud-functions';
import { UserDataStore } from '../store/userDataStore';
import TicketLogEntryForm from './TicketLogEntryForm';
import { ModalMetadata } from '../models/modal-metadata';
import GlobalDialog from './Modal/GlobalDialog';
import { DocumentationConstants } from '../store/documentation-constants';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { ItemBasicInformation } from '../models/item-basic-information';
import AuditHistoryComponent from './AuditHistoryComponent';
import { ToastProps } from '../models/toast-props';
import DmaFooter from './DmaFooter';

import DateBox from 'devextreme-react/date-box';
import TextBox from 'devextreme-react/text-box';
import NumberBox from 'devextreme-react/number-box';
import CheckBox from 'devextreme-react/check-box';
import { Toast } from 'devextreme-react/toast';
import { Button } from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';
import HtmlEditor from 'devextreme-react/html-editor';

const _PLANNED_BATCH_SIZE = 'plannedBatchSize';
const _ANALYTICAL_SAMPLES = 'analyticalSamples';
const _STRESSED_SAMPLES = 'stressedSamples';
const _RETENTION_SAMPLES = 'retentionSamples';
const _BATCH_SIZE_MULTIPLIER = 'batchSizeMultiplier';

let key = 1;

export default function BatchForm() {
    const [batch, setBatch] =
        useState<BatchPlan>();

    const [batchPlanningCalculation, setBatchPlanningCalculation] =
        useState<BatchPlanningCalculation>();

    const [targetWeightsUpdateKey, setTargetWeightsUpdateKey] =
        useState<number>(1);

    const [auditInformationDialogVisible, setAuditInformationDialogVisible] =
        useState<boolean>();

    const [ticketLogEntryDialogVisible, setTicketLogEntryDialogVisible] =
        useState<boolean>();

    const [auditEntity, setAuditEntity] =
        useState<Entities>();

    const [auditRowPointer, setAuditRowPointer] =
        useState<string>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>()

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    useEffect(() => {
        initTabs();
        getAllData();
    }, []);

    useEffect(() => {
        if (!batchPlanningCalculation)
            getBatchPlanningCalculation();
    }, [batch]);

    const parts = window.location.href.split('/');
    const batchId = parts[parts.length - 1];

    key++;

    const user = UserDataStore.getCurrentUser();
    const canEditRoles = ["SA", "MF-A", "PP-A", "PP-L", "BP-A", "BP-L", "BP-T"];
    const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canInsertRoles = ["SA", "MF-A", "PP-A", "PP-L", "BP-A", "BP-L"];
    const canInsert = canInsertRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canDeleteRoles = ["SA", "MF-A", "PP-A", "BP-A"];
    const canDelete = canDeleteRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

    let productionActualsPath = `/batch-production/${batchId}`;
    let calculatorPath = `/masterassaycalculator/${batchId}`;

    let batchDensityTitle = batch && batch.productData &&
        batch.productData.gravimetricCalculationType === GravimetricCalculationType.Neat ?
        "Literature Density" : "Estimated Batch Density";

    let fillInstructionUomString = batch && batch.productData &&
        batch.productData.targetFillUomString && batch.productData.targetFillUomString;

    let targetFillUom = batch && batch.productData &&
        batch.productData.targetFillUom;

    let nominalUnitSizeString = `${batch && batch.productData &&
        batch.productData.nominalUnitSize ? batch.productData.nominalUnitSize.toLocaleString('en-US') : 0} ` +
        `${fillInstructionUomString ? fillInstructionUomString : ''}`;

    let maximumFill = batch && (batch.productData && batch.productData.targetFill ?
        batch.productData.targetFill : 0) +
        (batch.productData && batch.productData.targetFillUncertainty ?
            batch.productData.targetFillUncertainty : 0);

    let fillSpec = `${batch && batch.productData &&
        batch.productData.targetFill ? batch.productData.targetFill : '0'} ± ` +
        `${batch && batch.productData &&
            batch.productData.targetFillUncertainty ? batch.productData.targetFillUncertainty : '0'} ` +
        `${fillInstructionUomString}`;

    let fullFillInstructions = `${fillSpec}`;

    let totalRetentionSamplesNeeded = batch ?
        ((batch.stressedSamples ? batch.stressedSamples : 0) +
            (batch.analyticalSamples ? batch.analyticalSamples : 0) +
            (batch.retentionSamples ? batch.retentionSamples : 0))
        : 0;

    let estimatedFill = batchPlanningCalculation && batchPlanningCalculation.targetBatchWeightCalculation &&
        batchPlanningCalculation.targetBatchWeightCalculation.estimatedFill ?
        `${batchPlanningCalculation.targetBatchWeightCalculation.estimatedFill.toLocaleString('en-US')} mL` : '0'

    let gravimetricCalculationType = batch && batch.productData &&
        batch.productData.gravimetricCalculationType;

    let createDate = batch && batch.createTimestamp ?
        PWDates.getLocalDateString(batch.createTimestamp) : 'NA';

    let approvalDate = batch && batch.approvalDate ?
        PWDates.getLocalDateString(batch.approvalDate) : '';

    // let targetBatchSize = (batch && batch.plannedBatchSize ?
    //   batch.plannedBatchSize : 0) + totalRetentionSamplesNeeded;

    return (
        <div className="pw-content">
            <BatchPlanningToolbar title="Batch Planning" batchId={batchId}
                entity={Entities.BatchPlanning} leftInfo="Catalog #"
                leftInfoSubtitle={batch && batch.productData ?
                    batch.productData.catalogNumber : ''}
                rightInfo="Batch Name"
                rightInfoSubtitle={batch && batch.batchName ?
                    batch.batchName : ''} showPrintButton={true}
                auditRowPointer={batchId}
                documentationId={DocumentationConstants.BatchPlanningForm}
                showItemInformation={showBatchInformationDialog} />

            <div id="pb-navigation-pane" >
                <div id="tab-buttons-panel" className="tab-buttons-container">
                    <div id="planningPanelButton" onClick={onPanelButtonClick}>Planning</div>
                    <div id="targetWeightsPanelButton" onClick={onPanelButtonClick}>Target Weights</div>
                    <div id="analyticalPlansPanelButton" onClick={onPanelButtonClick}>Analytical Plan</div>
                    <div id="standardsPanelButton" onClick={onPanelButtonClick}>Standards</div>
                    <div id="benchSheetsPanelButton" onClick={onPanelButtonClick}>QC Bench Sheets</div>
                    <div id="productionPanelButton" onClick={onPanelButtonClick}>Production</div>
                    <div id="calculationsReportPanelButton" onClick={onPanelButtonClick}>Calculations Report</div>
                    <div id="notesPanelButton" onClick={onPanelButtonClick}>Notes</div>


                    {/*  
                    <div id="safetyPanelButton" onClick={onPanelButtonClick}>Safety</div>
                    <div id="calculatorPanelButton" onClick={onPanelButtonClick}>Calculators</div>
                    */}

                </div>
            </div>

            <div id="pb-content" className="pw-margin-bottom-1">
                <div id="planningPanel" className="pw-form-section">
                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-10">
                            <div className="pw-form-unit-label">Created By</div>
                            <div className="pw-readonly-label">{batch && batch.createUserName ?
                                batch.createUserName : "NA"}</div>
                        </div>

                        <div className="pw-form-unit pw-comp-10">
                            <div className="pw-form-unit-label">Create Date</div>
                            <div className="pw-readonly-label">{createDate}</div>
                        </div>

                        {batch && batch.approvalDate &&
                            <div className="pw-form-unit pw-comp-10">
                                <div className="pw-form-unit-label">Approval Date</div>
                                <div className="pw-readonly-label">{approvalDate}</div>
                            </div>
                        }

                        {/*
                        {canDelete &&
                            <div className="pw-form-unit pw-comp-10">
                                <Button className="title-bar-button"
                                    text="Approve" icon="check" type="success"
                                    onClick={setApprovalDate} />
                            </div>
                        }
                        */}

                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Date Production Due</div>
                            <DateBox id="dateProductionDue"
                                type="date" readOnly={!canEdit}
                                displayFormat="MM/dd/yyyy"
                                showClearButton={true}
                                defaultValue={null}
                                onValueChanged={onBatchValueChanged}
                                placeholder="na" value={batch ? batch.dateProductionDue : ''} />
                        </div>

                        <div className="pw-form-unit pw-comp-10">
                            <div className="pw-form-unit-label">Work Order</div>
                            <TextBox id="popNumber" placeholder="na" readOnly={!canEdit}
                                value={batch ? batch.popNumber : undefined}
                                onValueChanged={onBatchValueChanged} />
                        </div>

                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">POP Due Date</div>
                            <DateBox id="popDueDate" type="date" readOnly={!canEdit}
                                displayFormat="MM/dd/yyyy"
                                showClearButton={true}
                                defaultValue={null}
                                value={batch ? batch.popDueDate : ''}
                                onValueChanged={onBatchValueChanged}
                                placeholder="na" />
                        </div>
                    </div>

                    <div className="pw-comp-row component-container">
                        <div className="pw-form-unit">
                            <div className="pw-form-unit-label">Components Actual Layout</div>
                            <div id="gravimetricCalculationLabel" className="pw-readonly-label" data-gravimetric-calculation={
                                batch && batch.productData ?
                                    batch.productData.gravimetricCalculationType : ''} >
                                {batch && batch.productData ?
                                    batch.productData.gravimetricCalculationName : ''}:
                                {batch && batch.productData ?
                                    batch.productData.gravimetricCalculationDescription : ''}
                            </div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Planned Batch Size (units)</div>
                            <NumberBox id={_PLANNED_BATCH_SIZE} readOnly={!canEdit}
                                format="#0.0000" placeholder="na"
                                onValueChanged={onBatchValueChanged}
                                value={batch ? batch.plannedBatchSize : 0} />
                        </div>
                    </div>

                    <div className="pw-comp-row component-container">
                        <div className="pw-form-unit pw-comp-10">
                            <div className="pw-form-unit-label">Analytical Samples</div>
                            <NumberBox id={_ANALYTICAL_SAMPLES} readOnly={!canEdit}
                                format="##0" placeholder="na"
                                onValueChanged={onBatchValueChanged}
                                value={batch ? batch.analyticalSamples : 0} />
                        </div>
                        <div className="pw-form-unit pw-comp-10">
                            <div className="pw-form-unit-label">Stressed Samples</div>
                            <NumberBox id={_STRESSED_SAMPLES} readOnly={!canEdit}
                                format="##0" placeholder="na"
                                onValueChanged={onBatchValueChanged}
                                value={batch ? batch.stressedSamples : 0} />
                        </div>

                        <div className="pw-form-unit pw-comp-10">
                            <div className="pw-form-unit-label">Retention Samples</div>
                            <NumberBox id={_RETENTION_SAMPLES} readOnly={!canEdit}
                                format="##0" placeholder="na"
                                onValueChanged={onBatchValueChanged}
                                value={batch ? batch.retentionSamples : 0} />
                        </div>

                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Total Reserved Samples</div>
                            <div className="pw-readonly-inline-label">{totalRetentionSamplesNeeded}</div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-12">
                            <div className="pw-form-unit-label">Pack Size</div>
                            <div className="pw-plus-minus-uom">{nominalUnitSizeString}</div>
                        </div>

                        <div className="pw-form-unit pw-comp-12">
                            <div className="pw-form-unit-label">Fill Specification</div>
                            <div className="pw-plus-minus-uom">{fillSpec}</div>
                        </div>

                        {/* PLEASE KEEP - WILL BE USED LATER
                                <div className="pw-form-unit pw-comp-15">
                                    <div className="pw-form-unit-label">Fill Specification</div>
                                    <div className="pw-plus-minus-container">
                                        <NumberBox id="targetFill"
                                            format="#0.000"
                                            onValueChanged={onBatchValueChanged}
                                            value={batch ? batch.targetFill : 0} />

                                        <div className="pw-readonly-label pw-auto-margin">+/-</div>

                                        <NumberBox id="fillUncertainty"
                                            format="#0.000"
                                            onValueChanged={onBatchValueChanged}
                                            value={batch ? batch.fillUncertainty : 0} />

                                        <div className="pw-plus-minus-uom">{fillInstructionUomString}</div>
                                    </div>
                                </div>
                                */}

                        <div className="pw-form-unit pw-comp-12">
                            <div className="pw-form-unit-label">Maximum Fill</div>
                            <div className="pw-plus-minus-uom">{maximumFill} {fillInstructionUomString}</div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-12">
                            <div className="pw-form-unit-label">Batch Size Multiplier</div>
                            <NumberBox id={_BATCH_SIZE_MULTIPLIER} readOnly={!canEdit}
                                format="#0.0000" placeholder="na"
                                onValueChanged={onBatchValueChanged}
                                value={batch ? batch.batchSizeMultiplier : 0} />
                        </div>
                    </div>

                    <div className="pw-comp-row component-container">


                        {/* used anymore?
                        {targetFillUom === Uoms.Milliliters &&
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Purging/Cleaning Estimate(mL)</div>
                                <NumberBox id={_PURGE_CLEANING_ESTIMATE} readOnly={!canEdit}
                                    placeholder="na" format="###0"
                                    defaultValue={0}
                                    onValueChanged={onBatchValueChanged}
                                    value={batch ? batch.purgeOrCleaningEstimate : 0} />
                            </div>
                        }
                        */}

                        <div className="pw-form-unit pw-comp-12">
                            <div className="pw-form-unit-label">Estimated Fill</div>
                            <div className="pw-readonly-label">{estimatedFill}</div>
                        </div>

                        {gravimetricCalculationType && gravimetricCalculationType === GravimetricCalculationType.WV &&
                            <div className="pw-form-unit pw-comp-12">
                                <div className="pw-form-unit-label">Closest Flask Size</div>
                                <div className="pw-plus-minus-uom">{batchPlanningCalculation &&
                                    batchPlanningCalculation.targetBatchWeightCalculation &&
                                    batchPlanningCalculation.targetBatchWeightCalculation.flaskVolume ?
                                    batchPlanningCalculation.targetBatchWeightCalculation.flaskVolume : 'NA'}</div>
                            </div>
                        }

                        <div className="pw-form-unit pw-comp-12">
                            <div className="pw-form-unit-label">{batchDensityTitle}</div>
                            <div className="pw-plus-minus-uom">{batch && batch.productData && batch.productData.estimatedBatchDensity ?
                                batch.productData.estimatedBatchDensity : 'NA'} (g/mL)</div>
                        </div>

                        <div className="pw-form-unit pw-comp-12">
                            <div className="pw-form-unit-label">Target Batch Weight(mg)</div>
                            <div className="pw-plus-minus-uom">{batchPlanningCalculation &&
                                batchPlanningCalculation.targetBatchWeightCalculation &&
                                batchPlanningCalculation.targetBatchWeightCalculation.targetBatchWeight ?
                                batchPlanningCalculation.targetBatchWeightCalculation.targetBatchWeight.toLocaleString('en-US') : 'NA'}</div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit">
                            <div className="pw-form-unit-label">Fill Instructions</div>
                            <div className="pw-readonly-label">{fullFillInstructions}</div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit">
                            <div className="pw-form-unit-label">Prep Instructions</div>
                            <div className="pw-readonly-label">{batch && batch.productData &&
                                batch.productData.prepInstructions ?
                                batch.productData.prepInstructions : 'NA'}</div>
                        </div>
                    </div>
                    <div className="pw-comp-row pw-margin-bottom-1">
                        <div className="pw-form-unit">
                            <CheckBox id="exhausted" className="pw-check-box"
                                text="Exhaust Batch" readOnly={!canDelete}
                                onValueChanged={onBatchValueChanged}
                                value={batch && batch.exhausted ?
                                    batch.exhausted : false} />
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit">
                            <div className="pw-form-unit-label">Maximum % Humidity</div>
                            <div className="pw-readonly-label">{batch && batch.productData && batch.productData.humidity ?
                                batch.productData.humidity : 'NA'}</div>
                        </div>

                        <div className="pw-form-unit">
                            <div className="pw-form-unit-label">Storage Requirements</div>
                            <div className="pw-readonly-label">{batch && batch.productData && batch.productData.storage ?
                                batch.productData.storage : 'NA'}</div>
                        </div>
                    </div>

                    <DmaFooter />

                </div>

                <div id="targetWeightsPanel" className="pw-form-section">
                    <BatchTargetWeightsData batchPlanningCalculation={batchPlanningCalculation} />

                    <DmaFooter />
                </div>

                <div id="analyticalPlansPanel">
                    {batch?.productId &&
                        <div className="pw-margin-top-05">
                            <BatchAnalyticalPlansComponent productId={batch && batch.productId} isReadOnly={!canEdit} />

                        </div>
                    }

                    <DmaFooter />
                </div>

                <div id="standardsPanel">
                    {batch?.productId &&
                        <div className="pw-margin-top-05">
                            <BatchStandardsData productId={batch.productId} isReadOnly={true} />
                        </div>
                    }

                    {/* NEED THIS?
                    <div>
                        <div className="lq-header pw-margin-top-05">
                            <div className="lq-header-title">Standards Documents</div>
                        </div>

                        <DocumentLoader parentId={batchId} documentType={DocumentType.StandardsDocument}
                            canDelete={canDelete} />
                    </div>
                    */}

                    <DmaFooter />
                </div>

                {/*
                <div id="safetyPanel" className="pw-form-section">

                </div>
                */}

                <div id="benchSheetsPanel" className="pw-form-section">
                    <BatchBenchSheetData batchId={batchId} productId={batch && batch.productId ?
                        batch.productId : undefined} isReadOnly={!canEdit} />

                    <DmaFooter />
                </div>

                {/*
                <div id="calculatorPanel" className="pw-form-section">
                    <DmaFooter />
                </div>
                */}


                <div id="productionPanel">
                    <div className="pw-comp-row pw-margin-top-1">
                        <div className="pw-form-unit">
                            <NavLink className="pw-card-link" to={productionActualsPath}>
                                <Button id="gotoProductionActualButton" text="Production Bench Sheet" icon="product" type="success"
                                    hint="go to Production Actuals"
                                    width='15rem'
                                    height='2.1rem' />
                            </NavLink>
                        </div>
                    </div>

                </div>

                <div id="calculationsReportPanel">
                    <div className="pw-comp-row pw-margin-top-1">

                        <HtmlEditor height="100%" readOnly={true}
                            value={batchPlanningCalculation &&
                                batchPlanningCalculation.executionDetails ?
                                batchPlanningCalculation.executionDetails : undefined} />
                    </div>
                </div>

                <div id="notesPanel">
                    <div className="pw-comp-row-scroll-fixed-grid">
                        <NoteViewer height='10rem' title="Batch Planning Notes"
                            noteType={NoteType.BatchPlanning} parentId={batchId} />
                    </div>

                    <div className="lq-sub-title pw-margin-top-1 pw-margin-bottom-05">Documents</div>
                    <DocumentLoader parentId={batchId}
                        documentType={DocumentType.BatchPlanning}
                        readOnly={!canEdit} canDelete={canDelete} />

                    <DmaFooter />

                </div>
            </div>

            {auditInformationDialogVisible &&
                <DialogPopup visible={auditInformationDialogVisible}
                    onHiding={hideAuditInformationDialog}
                    title="Audit Information"
                    minWidth={'50em'}
                    minHeight={'32em'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <AuditHistoryComponent entityNumber={auditEntity}
                        key={`AuditHistory${key}`}
                        rowPointer={auditRowPointer ? auditRowPointer : undefined} />

                    {/*
                    <AuditInformation key={`ai${key}`} entity="BatchPlanning"
                        keyId={batchId ? batchId : undefined} />
                        */}
                </DialogPopup>
            }

            {ticketLogEntryDialogVisible &&
                <DialogPopup visible={ticketLogEntryDialogVisible}
                    onHiding={hideTicketLogEntryDialog}
                    title="Ticket Log Entry"
                    width={'55rem'}
                    height={'45rem'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <TicketLogEntryForm formName="Product Planning" contextData={batch}
                        onCancel={hideTicketLogEntryDialog}
                        onComplete={hideTicketLogEntryDialog}
                        key={`TLE${key}`} />
                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {toastProps && toastProps.visible &&
                <Toast maxWidth="40rem"
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={4000} />
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    //change events

    /*
    async function setApprovalDate() {
        if (!e.event)
            return;

        setBatch({ ...batch, approvalDate: new Date() });

        let fieldUpdate: FieldUpdate = {
            field: "approvalDate",
            value: new Date(),
            rowId: batchId
        };

        await updateBatchField(fieldUpdate)
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Setting Approval Date");
            });
    }
    */

    async function onBatchValueChanged(e: any) {
        if (!e.event)
            return;

        const caller = e.element.id;
        let prevValue = e.previousValue;

        setBatch({ ...batch, [caller]: e.value });
        //this tells the Target Weights component to rerun the calculation
        setTargetWeightsUpdateKey(targetWeightsUpdateKey + 1);

        let fieldUpdate: FieldUpdate = {
            field: caller,
            value: e.value,
            rowId: batchId
        };

        await updateBatchField(fieldUpdate)
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating Batch Field");
                setBatch({ ...batch, [caller]: prevValue });
            });
    }

    // GET functions

    async function getBatch() {
        await CrudFunctions.getData(`products/batchplan/${batchId}`)
            .then(data => {
                //console.log("BatchForm, Batch:");
                //console.log(data);

                setBatch(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Batch Data");
            });
    }

    async function getBatchPlanningCalculation() {
        await CrudFunctions.getData(`calculations/batchplanningcalculation/${batchId}`)
            .then(data => {
                //console.log("CALCULATION:");
                //console.log(data);

                setBatchPlanningCalculation(data);

                if (data.calculationMessage)
                    showToast(data.calculationMessage, "warning");

            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Calculation Data");
            });
    }

    // PUT functions

    async function updateBatchField(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("products/batchplanfield", fieldUpdate);
    }

    async function getAllData() {
        await Promise.all([
            getBatch()
        ]);
    }

    //show/hide

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    function showAuditInformationDialog() {
        setAuditEntity(Entities.BatchPlanning);
        setAuditRowPointer(batchId);
        setAuditInformationDialogVisible(true);
    }

    function hideAuditInformationDialog() {
        setAuditInformationDialogVisible(false);
    }

    function showTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(true);
    }

    function hideTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(false);
    }

    function showBatchInformationDialog() {
        if (batch) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: batchId,
                createTimestamp: batch.createTimestamp,
                lastModifiedTimestamp: batch.modifyTimestamp,
                createUser: batch.createUser,
                modifyUser: batch.modifyUser
            };

            setItemBasicInformation(itemInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    //other

    function onPanelButtonClick(e: any) {
        resetAllButtons();

        const button = e.target as HTMLDivElement;

        if (button) {
            button.className = "tab-button-active";
        }

        hideAllComponents();

        const id: string = e.target.id;
        const panel = document.getElementById(id.replace("Button", "")) as HTMLDivElement;
        panel.style.display = "";
    }

    function resetAllButtons() {
        const planningPanelButton = document.getElementById("planningPanelButton") as HTMLDivElement;
        planningPanelButton.className = "tab-button-inactive";

        const targetWeightsButton = document.getElementById("targetWeightsPanelButton") as HTMLDivElement;
        targetWeightsButton.className = "tab-button-inactive";

        const analyticalPlansPanelButton = document.getElementById("analyticalPlansPanelButton") as HTMLDivElement;
        analyticalPlansPanelButton.className = "tab-button-inactive";

        const standardsButton = document.getElementById("standardsPanelButton") as HTMLDivElement;
        standardsButton.className = "tab-button-inactive";

        // const safetyPanelButton = document.getElementById("safetyPanelButton") as HTMLDivElement;
        // safetyPanelButton.className = "tab-button-inactive";

        const benchSheetsPanelButton = document.getElementById("benchSheetsPanelButton") as HTMLDivElement;
        benchSheetsPanelButton.className = "tab-button-inactive";

        // const calculatorPanelButton = document.getElementById("calculatorPanelButton") as HTMLDivElement;
        // calculatorPanelButton.className = "tab-button-inactive";

        const productionPanelButton = document.getElementById("productionPanelButton") as HTMLDivElement;
        productionPanelButton.className = "tab-button-inactive";

        const calculationsReportPanelButton = document.getElementById("calculationsReportPanelButton") as HTMLDivElement;
        calculationsReportPanelButton.className = "tab-button-inactive";

        const notesPanelButton = document.getElementById("notesPanelButton") as HTMLDivElement;
        notesPanelButton.className = "tab-button-inactive";
    }

    function hideAllComponents(onload: boolean = false) {
        if (!onload) {
            const planningPanel = document.getElementById("planningPanel") as HTMLDivElement;
            planningPanel.style.display = "none";
        }

        const targetWeightsPanel = document.getElementById("targetWeightsPanel") as HTMLDivElement;
        targetWeightsPanel.style.display = "none";

        const analyticalPlansPanel = document.getElementById("analyticalPlansPanel") as HTMLDivElement;
        analyticalPlansPanel.style.display = "none";

        const standardsPanel = document.getElementById("standardsPanel") as HTMLDivElement;
        standardsPanel.style.display = "none";

        // const safetyPanel = document.getElementById("safetyPanel") as HTMLDivElement;
        // safetyPanel.style.display = "none";

        const benchSheetsPanel = document.getElementById("benchSheetsPanel") as HTMLDivElement;
        benchSheetsPanel.style.display = "none";

        // const calculatorPanel = document.getElementById("calculatorPanel") as HTMLDivElement;
        // calculatorPanel.style.display = "none";

        const productionPanel = document.getElementById("productionPanel") as HTMLDivElement;
        productionPanel.style.display = "none";

        const calculationsReportPanel = document.getElementById("calculationsReportPanel") as HTMLDivElement;
        calculationsReportPanel.style.display = "none";

        const notesPanel = document.getElementById("notesPanel") as HTMLDivElement;
        notesPanel.style.display = "none";
    }

    function initTabs() {
        hideAllComponents(true);

        const planningPanelTab = document.getElementById("planningPanelButton") as HTMLDivElement;
        planningPanelTab.click();
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }


}
